import {
    Component,
    ViewEncapsulation,
    OnInit,
    AfterViewInit,
    OnDestroy,
    ElementRef,
    Input,
    Output,
    EventEmitter
} from '@angular/core';

import {
    timer,
    Subject,
    Observable,
    combineLatest
} from 'rxjs';

import {
    tap,
    skip,
    filter,
    debounce,
    takeUntil,
    map,
    distinctUntilChanged
} from 'rxjs/operators';

import * as Screeps from '@screeps/map';

let __rooms__ = {};
let __decorations__ = {};

@Component({
    selector: 'app-world-map-map',
    templateUrl: './map.component.pug',
    styleUrls: ['./map.component.less']
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {
    _size: {
        width: number;
        height: number;
    };
    @Input() set size(value) {
        this._size = value;
    }

    get nativeElement() {
        return this._ref.nativeElement;
    }

    @Output() bound: EventEmitter<any> = new EventEmitter();
    @Output() scaleChange: EventEmitter<any> = new EventEmitter();
    @Output() centerChange: EventEmitter<any> = new EventEmitter();
    @Output() roomOver: EventEmitter<any> = new EventEmitter();
    @Output() roomClick: EventEmitter<any> = new EventEmitter();

    screepsMap: Screeps.Map;
    roomsCache = {};

    _destroySbj: Subject<void> = new Subject();

    bound$: Observable<any>;

    constructor(
        private _ref: ElementRef
    ) {
    }

    ngOnInit() {
        this.screepsMap = new Screeps.Map(this._ref.nativeElement);

        // TODO: Need to add fix when in one time changes bound and size
        //  now it execute double
        //  use case: User change novice area by UI which content data like it ['shard0/W1N1', 'shard1/W30N30']
        const _bound$ = this.screepsMap.bound$
            .pipe(filter((bound) => !!bound))
            .pipe(distinctUntilChanged((o, n) => Screeps.equal(o, n)))
            .pipe(debounce(() => timer(100)));

        const _size$ = this.screepsMap.size$
            .pipe(filter((size) => !!size))
            .pipe(distinctUntilChanged((o, n) => Screeps.equal(o, n)));

        this.bound$ = combineLatest(_bound$, _size$)
            .pipe(map(([bound]) => bound));
            // .pipe(map(([bound, size]) => Screeps.cutBound(bound, size)));
    }

    ngAfterViewInit() {
        this.bound$
            .pipe(takeUntil(this._destroySbj))
            .subscribe();

        this.screepsMap.scale$
            .pipe(takeUntil(this._destroySbj))
            .pipe(skip(1))
            .pipe(tap((scale: number) => this.onScale(scale)))
            .subscribe();

        this.screepsMap.center$
            .pipe(takeUntil(this._destroySbj))
            .pipe(skip(1))
            .pipe(tap((center: [number, number]) => this.onCenter(center)))
            .subscribe();

        this.screepsMap.roomOver$
            .pipe(takeUntil(this._destroySbj))
            .pipe(skip(1))
            .pipe(tap((data) => this.onRoomOver(data)))
            .subscribe();

        this.screepsMap.roomClick$
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap((data) => this.onRoomClick(data)))
            .subscribe();
    }

    ngOnDestroy() {
        this._destroySbj.next();
        this._destroySbj.complete();

        this.screepsMap.destroy();
    }

    setSize(size: any) {
        __rooms__ = {};
        __decorations__ = {};

        this.screepsMap.setSize(size);
    }

    setScale(scale: number) {
        this.screepsMap.setScale(scale);
    }

    setCenter(center: [number, number]) {
        this.screepsMap.setCenter(center);
    }

    getLayer(layer: string) {
        return this.screepsMap.getLayer(layer);
    }

    drawRooms(rooms: IRoom[]) {
        rooms.forEach(({ room }) => {
            __rooms__[room] = true;
        });

        rooms = rooms.filter(({ terrain }) => {
            return !!terrain;
        });

        this.screepsMap.drawRooms(rooms);
    }

    drawStats(stats: any) {
        for (const { room, stat } of stats) {
            if (Screeps.isSafeMode(stat)) {
                this.screepsMap.getLayer(Screeps.SAFE_MODE_LAYER).draw(room, stat);
            }
        }

        this.screepsMap.drawStats(stats);
    }

    drawUsers(users: any[]) {
        this.screepsMap.drawUsers(users);
    }

    drawUnits(room: string, data: any) {
        this.screepsMap.drawUnits(room, data);
    }

    drawDecorations(decorations: any[]) {
        decorations = decorations.filter(({ room, decorations }) => {
            const _room = __decorations__[room] || {};
            const changes = decorations.some(({ decoration: { _id, activatedAt } }) => _room[_id] !== activatedAt);

            if (!changes) {
                return false;
            }

            __decorations__[room] = decorations.reduce((acc, { decoration: { _id, activatedAt } }) => ({
                ...acc,
                [_id]: activatedAt
            }), {});

            return true;
        });

        this.screepsMap.drawDecorations(decorations);
    }

    drawMinerals(stats: any) {
        this.screepsMap.drawMinerals(stats);
    }

    toggleLayer(layer: string, renderable: boolean) {
        this.screepsMap.toggleLayer(layer, renderable);
    }

    toggleUnits(renderable: boolean) {
        this.screepsMap.toggleUnitsLayer(renderable);
    }

    toggleStats(renderable: boolean) {
        this.screepsMap.toggleStatsLayer(renderable);
    }

    toggleUsers(renderable: boolean) {
        this.screepsMap.toggleUsersLayer(renderable);
    }

    toggleMinerals(renderable: boolean) {
        this.screepsMap.toggleMineralsLayer(renderable);
    }

    onBound(bound: any) {
        this.bound.emit(bound);
    }

    onScale(scale: number) {
        this.scaleChange.emit(scale);
    }

    onCenter(center: [number, number]) {
        this.centerChange.emit(center);
    }

    onRoomOver(data) {
        this.roomOver.emit(data);
    }

    onRoomClick(data) {
        this.roomClick.emit(data);
    }

    resize() {
        this.screepsMap.resize();
    }

    render() {
        this.screepsMap.render();
    }

    contain(room: string) {
        return !!__rooms__[room];
    }
}
